import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ModalProvider } from './ModalContext';
import Search from "./components/Search";
import Settings from "./components/Settings";
import HowToUse from "./components/HowToUse";
import PlaylistPlayer from "./components/PlaylistPlayer";
import ShareSong from "./components/ShareSong";
import Share from "./components/Share";
import SharePage from "./components/SharePage";
import Radio from "./components/Radio";
import RadioMobile from "./components/RadioMobile";
import LoginModal from "./components/LoginModal";
import RegisterModal from "./components/RegisterModal";
import ResetPasswordModal from "./components/ResetPasswordModal";
import ResetPasswordPage from "./components/ResetPasswordPage";
import PlaylistPageWithPlayer from './components/PlaylistPageWithPlayer';
import SharedPlaylistPage from './components/SharedPlaylistPage';
import SharedFavoritePage from './components/SharedFavoritePage';
import FavoritePageWithPlayer from './components/FavoritePageWithPlayer';
import RidoPage from './components/RadioPage';
import RadioPageWithPlayer from './components/RadioPageWithPlayer';
import SettingsPage from './components/SettingsPage';
import AdminPage from './components/AdminPage';
import ReadingPlansPage from './components/ReadingPlansPage';
import BibleNotesPage from './components/BibleNotesPage';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

// 版本文件：放网站根目录（使用 server.url 时），
// 或放到 React 工程 public/ 下（打包到本地时）
const VERSION_URL = '/version.json';

// 仅原生环境：卸载所有 Service Worker，并清空 caches
async function disableSWAndCachesOnNative() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    }
    if (window.caches?.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
    // 给你一个可见的确认
    console.log('[Native] SW & caches cleared');
  } catch (e) {
    console.warn('disableSWAndCachesOnNative error:', e);
  }
}

// 拉取版本号，变更则强制刷新
async function checkVersionAndReload() {
  try {
    // 防缓存
    const r = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!r.ok) return;
    const { build } = await r.json(); // { "build": "2025-08-30T12:34:56Z" }
    if (!build) return;
    const last = localStorage.getItem('web_build');
    const currentUrl = new URL(window.location.href);
    const loadedBuild = currentUrl.searchParams.get('_appBuild');

    // WebView 的普通 location.reload() 可能继续复用旧 index.html。
    // 在网址加入当前构建号，让每次发布都产生新的页面地址并绕过 HTTP 缓存。
    if (loadedBuild !== build) {
      console.log('[Native] Loading current web build:', build, 'recorded:', last, 'url:', loadedBuild);
      localStorage.setItem('web_build', build);
      currentUrl.searchParams.set('_appBuild', build);
      window.location.replace(currentUrl.toString());
      return;
    }

    localStorage.setItem('web_build', build);
  } catch (e) {
    // 离线/网络错误时忽略
    console.debug('checkVersionAndReload skip:', e?.message || e);
  }
}

// 在根组件里调用的 Hook
export function useNativeWebAutoUpdate() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    const prepareCurrentWebBuild = async () => {
      // 先清理 Service Worker/Cache Storage，再检查线上构建号。
      await disableSWAndCachesOnNative();
      if (!cancelled) await checkVersionAndReload();
    };
    prepareCurrentWebBuild();

    // 3) 从后台回到前台再检查（拿到发布后的最新前端）
    let listenerHandle = null;
    Promise.resolve(CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) checkVersionAndReload();
    })).then(handle => {
      if (cancelled) handle.remove();
      else listenerHandle = handle;
    });
    return () => {
      cancelled = true;
      listenerHandle?.remove();
    };
  }, []);
}

function useNativeShareLinks() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const openSharedPage = ({ url }) => {
      try {
        const target = new URL(url);
        const isCheckTheBibleHost = ['www.checkthebible.com', 'checkthebible.com'].includes(target.hostname);
        const isSharedPage = /^\/(sharedfavorite|sharedplaylist)\//i.test(target.pathname)
          || /^\/reading-plans\/shared\//i.test(target.pathname);
        if (!isCheckTheBibleHost || !isSharedPage) return;

        window.location.assign(`${target.pathname}${target.search}${target.hash}`);
      } catch (error) {
        console.warn('Unable to open shared link:', error);
      }
    };

    let removed = false;
    let listenerHandle = null;
    Promise.resolve(CapApp.addListener('appUrlOpen', openSharedPage)).then(handle => {
      if (removed) handle.remove();
      else listenerHandle = handle;
    });
    return () => {
      removed = true;
      listenerHandle?.remove();
    };
  }, []);
}

function useAppViewportLayout() {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('native-app', Capacitor.isNativePlatform());

    let frameId = 0;
    let maximumHeight = 0;
    let previousWidth = window.innerWidth;

    const updateViewport = () => {
      cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        const viewport = window.visualViewport;

        const visibleHeight = Math.round(
          viewport?.height ?? window.innerHeight
        );

        const visibleWidth = Math.round(
          viewport?.width ?? window.innerWidth
        );

        // 横竖屏切换或窗口宽度明显改变时，重新计算基准高度
        if (Math.abs(visibleWidth - previousWidth) > 80) {
          maximumHeight = visibleHeight;
          previousWidth = visibleWidth;
        }

        maximumHeight = Math.max(maximumHeight, visibleHeight);

        root.style.setProperty(
          '--app-viewport-height',
          `${visibleHeight}px`
        );

        // 键盘打开后，可见高度一般会明显变小
        const keyboardOpen =
          maximumHeight - visibleHeight > 120;

        root.classList.toggle(
          'keyboard-open',
          keyboardOpen
        );
      });
    };

    updateViewport();

    window.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener(
      'resize',
      updateViewport
    );

    return () => {
      cancelAnimationFrame(frameId);

      window.removeEventListener(
        'resize',
        updateViewport
      );

      window.visualViewport?.removeEventListener(
        'resize',
        updateViewport
      );

      root.classList.remove('keyboard-open');
      root.classList.remove('native-app');
    };
  }, []);
}

export default function App() {
  useNativeWebAutoUpdate();
  useNativeShareLinks();
  useAppViewportLayout();

  return (
    <ModalProvider>
      <Router>
        <div className="appShell">
          <main className="appViewport">
            <Routes>
              <Route path="/howtouse" element={<HowToUse />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settingspage" element={<SettingsPage />} />
              <Route path="/player" element={<PlaylistPlayer />} />
              <Route
                path="/playerpage"
                element={<PlaylistPageWithPlayer />}
              />
              <Route path="/sharesong" element={<ShareSong />} />
              <Route path="/share" element={<Share />} />
              <Route path="/sharepage" element={<SharePage />} />
              <Route
                path="/favoritepage"
                element={<FavoritePageWithPlayer />}
              />
              <Route path="/radio" element={<Radio />} />
              <Route path="/radiopage" element={<RidoPage />} />
              <Route
                path="/radioeditpage"
                element={<RadioPageWithPlayer />}
              />
              <Route path="/radiomobile" element={<RadioMobile />} />
              <Route
                path="/reset-password"
                element={<ResetPasswordPage />}
              />
              <Route path="/adminpage" element={<AdminPage />} />
              <Route path="/reading-plans" element={<ReadingPlansPage />} />
              <Route path="/bible-notes" element={<BibleNotesPage />} />
              <Route
                path="/reading-plans/shared/:shareCode"
                element={<ReadingPlansPage />}
              />

              <Route
                path="/sharedplaylist/:playlistId"
                element={<SharedPlaylistPage />}
              />
              <Route
                path="/sharedfavorite/:playlistId"
                element={<SharedFavoritePage />}
              />

              <Route path="/home" element={<Search />} />
              <Route path="/" element={<Search />} />
            </Routes>
          </main>

          <LoginModal />
          <RegisterModal />
          <ResetPasswordModal />
        </div>
      </Router>
    </ModalProvider>
  );
}


